// src/components/Profile.js
import React, { useContext, useEffect, useState, useRef } from 'react';
import { format } from 'date-fns';
import LockIcon from '@mui/icons-material/Lock';
import SecurityIcon from '@mui/icons-material/Security';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import BadgeIcon from '@mui/icons-material/Badge';
import EditIcon from '@mui/icons-material/Edit';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import CloseIcon from '@mui/icons-material/Close';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import {
  Card,
  CardContent,
  Button,
  Stack,
  Box,
  Typography,
  Grid,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Divider,
  IconButton,
  Tooltip,
  Breadcrumbs,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setUser } from '../store/reducers/user';
import { ColorModeContext } from '../index';
import { request } from '../utils/request';

function Profile() {
  const { mode } = useContext(ColorModeContext);
  const isDark = mode === 'dark';
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);

  const [currentUser, setCurrentUser] = useState(() => {
    try {
      const stored = localStorage.getItem('user');
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });

  // Helpers
  const capitalize = (s = '') =>
    String(s)
      .split(' ')
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ')
      .trim();

  // Name formatting: "Amlan D."
  const firstName = currentUser.first_name || '';
  const lastName = currentUser.last_name || '';
  let rawName = currentUser.name || '';
  if (!rawName && (firstName || lastName)) {
    rawName = `${firstName} ${lastName}`.trim();
  }
  let displayName = rawName || 'Unknown User';
  if (rawName) {
    const parts = rawName.trim().split(/\s+/);
    const f = parts[0] || '';
    const last = parts.slice(1).join(' ');
    if (last) {
      displayName = `${capitalize(f)} ${capitalize(last)[0] ? capitalize(last)[0] + '.' : ''}`;
    } else {
      displayName = capitalize(f);
    }
  }

  const role = currentUser.role || '';
  const phone = currentUser.mobile || currentUser.phone || '—';
  const department = currentUser.department || '—';
  const empId = currentUser.emp_id || currentUser.empId || currentUser.empID || '—';
  const designation = currentUser.designation || '—';

  // Avatar state
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [avatarLoading, setAvatarLoading] = useState(false);

  // Edit Modal State
  const [editOpen, setEditOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editFormData, setEditFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    mobile: '',
    designation: '',
    emp_id: '',
    profile_image_file: null,
    profile_image_preview: null,
  });

  // Snack state
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const showSnack = (message, severity = 'success') => {
    setSnack({ open: true, message, severity });
  };

  // Fetch full user details and signed avatar on mount
  useEffect(() => {
    let mounted = true;
    const userId = currentUser && (currentUser._id || currentUser.id);

    const fetchUserDetails = async () => {
      if (!userId || currentUser.superadmin) return;
      try {
        const res = await request('user', { method: 'get', params: userId });
        const data = (res && res.user) || (res && res.data) || res;
        if (mounted && data) {
          setCurrentUser(prev => {
            const merged = { ...prev, ...data };
            if (!merged.name && (data.first_name || data.last_name)) {
              merged.name = `${data.first_name || ''} ${data.last_name || ''}`.trim();
            }
            try {
              localStorage.setItem('user', JSON.stringify(merged));
            } catch (e) {}
            return merged;
          });
          if (data.signedUrl) {
            setAvatarUrl(data.signedUrl);
          }
        }
      } catch (err) {
        console.error('Failed to fetch user details:', err);
      }
    };

    const fetchAvatar = async () => {
      if (!userId) return;
      setAvatarLoading(true);
      try {
        const data = await request('user', { method: 'get', params: `${userId}/avatar` });
        if (mounted && data && data.url) {
          setAvatarUrl(data.url);
        }
      } catch (err) {
        console.error('Failed to fetch avatar signed URL', err);
        setAvatarUrl(null);
      } finally {
        if (mounted) setAvatarLoading(false);
      }
    };

    fetchUserDetails();
    fetchAvatar();
    return () => {
      mounted = false;
    };
  }, [currentUser._id, currentUser.id]);

  // Devices state
  const [devices, setDevices] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(false);

  useEffect(() => {
    let mounted = true;
    const userId = currentUser && (currentUser._id || currentUser.id);
    if (!userId) {
      setDevices([]);
      return;
    }

    const fetchSessions = async () => {
      setDevicesLoading(true);
      try {
        const res = await request('sessions', { method: 'get', query: { userId, limit: 100 } });
        const sessions = (res && res.data) || [];
        const map = sessions.reduce((acc, s) => {
          const rawDevice = (s.deviceId || s.deviceid || (s.devicePosture && s.devicePosture.deviceId) || '').toString().trim();
          if (!rawDevice) return acc;
          const device = rawDevice;
          const loginTime = s.loginTime ? new Date(s.loginTime) : (s.createdAt ? new Date(s.createdAt) : null);
          const prev = acc[device];
          if (!prev || (loginTime && loginTime > prev.lastSeen)) {
            acc[device] = { deviceId: device, lastSeen: loginTime || new Date(0) };
          }
          return acc;
        }, {});
        const list = Object.values(map)
          .sort((a, b) => b.lastSeen - a.lastSeen)
          .map(it => ({ ...it, lastSeen: it.lastSeen instanceof Date ? it.lastSeen.toISOString() : it.lastSeen }));
        if (mounted) setDevices(list);
      } catch (err) {
        console.error('Failed to fetch sessions for devices:', err);
        if (mounted) setDevices([]);
      } finally {
        if (mounted) setDevicesLoading(false);
      }
    };

    fetchSessions();

    return () => {
      mounted = false;
    };
  }, [currentUser._id, currentUser.id]);

  const detailIconMapping = {
    person: { icon: <PersonIcon sx={{ color: 'var(--edge-blue)' }} />, bg: 'var(--blue-tint)' },
    badge: { icon: <BadgeIcon sx={{ color: '#16A34A' }} />, bg: '#DCFCE7' },
    devices: { icon: <DesktopWindowsIcon sx={{ color: '#7C3AED' }} />, bg: '#EDE9FE' }
  };

  // Open Edit Profile Modal
  const handleOpenEdit = () => {
    let fName = currentUser.first_name || '';
    let lName = currentUser.last_name || '';
    if (!fName && !lName) {
      const fallbackName = currentUser.name || currentUser.username || '';
      if (fallbackName) {
        const parts = fallbackName.trim().split(/\s+/);
        fName = parts[0] || '';
        lName = parts.slice(1).join(' ') || '';
      }
    }

    setEditFormData({
      first_name: fName,
      last_name: lName,
      email: currentUser.email || '',
      mobile: currentUser.mobile || currentUser.phone || '',
      designation: currentUser.designation && currentUser.designation !== '—' ? currentUser.designation : '',
      emp_id: currentUser.emp_id || currentUser.empId || currentUser.empID || '',
      profile_image_file: null,
      profile_image_preview: avatarUrl,
    });
    setEditOpen(true);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    if (name === 'mobile') {
      const cleaned = value.replace(/\D/g, '').slice(0, 10);
      setEditFormData(prev => ({ ...prev, [name]: cleaned }));
      return;
    }
    if (name === 'emp_id') {
      const cleaned = value.slice(0, 8);
      setEditFormData(prev => ({ ...prev, [name]: cleaned }));
      return;
    }
    setEditFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAvatarFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      const preview = URL.createObjectURL(file);
      setEditFormData(prev => ({
        ...prev,
        profile_image_file: file,
        profile_image_preview: preview,
      }));
    }
  };

  const handleSaveProfile = async (e) => {
    e?.preventDefault();
    const userId = currentUser._id || currentUser.id;
    if (!userId) {
      showSnack('User ID not found.', 'error');
      return;
    }

    if (!editFormData.first_name.trim()) {
      showSnack('First Name is required.', 'error');
      return;
    }
    if (editFormData.mobile && editFormData.mobile.length > 10) {
      showSnack('Mobile number cannot exceed 10 digits.', 'error');
      return;
    }
    if (editFormData.emp_id && editFormData.emp_id.length > 8) {
      showSnack('Employee ID cannot exceed 8 digits.', 'error');
      return;
    }

    setEditLoading(true);
    try {
      let payload;
      if (editFormData.profile_image_file) {
        payload = new FormData();
        payload.append('first_name', editFormData.first_name);
        payload.append('last_name', editFormData.last_name);
        payload.append('mobile', editFormData.mobile);
        payload.append('designation', editFormData.designation);
        payload.append('emp_id', editFormData.emp_id);
        payload.append('profile_image', editFormData.profile_image_file);
      } else {
        payload = {
          first_name: editFormData.first_name,
          last_name: editFormData.last_name,
          mobile: editFormData.mobile,
          designation: editFormData.designation,
          emp_id: editFormData.emp_id,
        };
      }

      const res = await request('user', {
        method: 'put',
        params: userId,
        body: payload,
      });

      const updatedData = (res && res.user) || (res && res.data) || res || {};
      const mergedUser = {
        ...currentUser,
        ...updatedData,
        first_name: editFormData.first_name,
        last_name: editFormData.last_name,
        mobile: editFormData.mobile,
        designation: editFormData.designation,
        emp_id: editFormData.emp_id,
        name: `${editFormData.first_name} ${editFormData.last_name}`.trim(),
      };

      localStorage.setItem('user', JSON.stringify(mergedUser));
      dispatch(setUser(mergedUser));
      setCurrentUser(mergedUser);

      if (res?.signedUrl) {
        setAvatarUrl(res.signedUrl);
      } else if (editFormData.profile_image_preview && editFormData.profile_image_file) {
        setAvatarUrl(editFormData.profile_image_preview);
      }

      showSnack('Profile updated successfully!', 'success');
      setEditOpen(false);
    } catch (err) {
      console.error('Failed to update profile:', err);
      showSnack(err?.response?.data?.message || err?.message || 'Failed to update profile', 'error');
    } finally {
      setEditLoading(false);
    }
  };

  const inputStyle = {
    width: '100%',
    '& .MuiInputBase-root': {
      backgroundColor: isDark ? '#1E293B' : '#ffffff',
      borderRadius: '8px',
      color: isDark ? '#F8FAFC' : '#0F172A',
      fontSize: '14px',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : '#E2E8F0',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: isDark ? 'rgba(255, 255, 255, 0.2) !important' : '#CBD5E1 !important',
    },
    '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#3B82F6 !important',
    },
    '& .Mui-disabled': {
      backgroundColor: isDark ? 'rgba(255, 255, 255, 0.04) !important' : '#F1F5F9 !important',
      color: isDark ? '#64748B !important' : '#94A3B8 !important',
      WebkitTextFillColor: isDark ? '#64748B !important' : '#94A3B8 !important',
      cursor: 'not-allowed',
    },
  };

  const ProfileSummary = ({ displayName, designation, email, phone }) => (
    <Card sx={{ boxShadow: 'none', border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid var(--hairline)', borderRadius: '16px', backgroundColor: isDark ? 'var(--canvas)' : '#FFF' }}>
      <CardContent sx={{ p: 4, textAlign: 'center' }}>
        <Box sx={{ position: 'relative', display: 'inline-block', mb: 2 }}>
          {avatarLoading ? (
            <CircularProgress size={100} />
          ) : avatarUrl ? (
            <Box component="img" src={avatarUrl} alt="profile" sx={{ width: 100, height: 100, borderRadius: '50%', objectFit: 'cover' }} />
          ) : (
            <Box sx={{ width: 100, height: 100, borderRadius: '50%', backgroundColor: 'var(--blue-tint)', color: 'var(--edge-blue)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2.5rem', fontWeight: 600 }}>
              {displayName ? displayName[0] : 'U'}
            </Box>
          )}
          <Tooltip title="Edit profile image">
            <IconButton
              size="small"
              onClick={handleOpenEdit}
              sx={{
                position: 'absolute',
                right: 0,
                bottom: 0,
                backgroundColor: isDark ? '#1e293b' : '#fff',
                border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid var(--hairline)',
                width: 32,
                height: 32,
                '&:hover': { backgroundColor: isDark ? '#334155' : '#f8fafc' },
              }}
            >
              <EditIcon sx={{ fontSize: 16, color: isDark ? '#94A3B8' : '#64748B' }} />
            </IconButton>
          </Tooltip>
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: isDark ? '#F8FAFC' : 'var(--vault-navy)' }}>
          {displayName}
        </Typography>
        <Button
          variant="outlined"
          startIcon={<EditIcon sx={{ fontSize: 18 }} />}
          onClick={handleOpenEdit}
          sx={{
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            mb: 3,
            borderColor: 'var(--blue-tint)',
            backgroundColor: 'var(--blue-tint)',
            color: 'var(--edge-blue)',
            '&:hover': { backgroundColor: 'var(--blue-tint)', opacity: 0.9, border: '1px solid var(--edge-blue)' },
            border: '1px solid var(--edge-blue)',
          }}
        >
          Edit Profile
        </Button>

        <Divider sx={{ mb: 3, borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'var(--hairline)' }} />

        <Box sx={{ textAlign: 'left' }}>
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <Box sx={{ mt: 0.5 }}>
              <EmailIcon sx={{ color: isDark ? '#94A3B8' : '#1e293b', fontSize: 20 }} />
            </Box>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, color: isDark ? '#F8FAFC' : 'var(--vault-navy)', mb: 0.5 }}>Email Address</Typography>
              <Typography variant="body2" sx={{ color: isDark ? '#94A3B8' : '#64748B' }}>{email || '—'}</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ mt: 0.5 }}>
              <PhoneIcon sx={{ color: isDark ? '#94A3B8' : '#1e293b', fontSize: 20 }} />
            </Box>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, color: isDark ? '#F8FAFC' : 'var(--vault-navy)', mb: 0.5 }}>Phone Number</Typography>
              <Typography variant="body2" sx={{ color: isDark ? '#94A3B8' : '#64748B' }}>{phone || '—'}</Typography>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const DetailCard = ({ title, icon, items, action }) => (
    <Card sx={{ boxShadow: 'none', border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid var(--hairline)', borderRadius: '16px', mb: 3, backgroundColor: isDark ? 'var(--canvas)' : '#FFF' }}>
      <CardContent sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: '8px', backgroundColor: detailIconMapping[icon]?.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {detailIconMapping[icon]?.icon}
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: isDark ? '#F8FAFC' : 'var(--vault-navy)' }}>
              {title}
            </Typography>
          </Box>
          {action}
        </Box>
        <Grid container rowSpacing={3} columnSpacing={6}>
          {items.map((item, idx) => (
            <Grid item xs={12} md={6} key={idx}>
              <Box sx={{ display: 'flex' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: isDark ? '#94A3B8' : '#64748B', width: '130px', flexShrink: 0 }}>
                  {item.label}
                </Typography>
                <Typography variant="body2" sx={{ color: isDark ? '#F8FAFC' : 'var(--vault-navy)', fontWeight: 500 }}>
                  {item.value || '—'}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ backgroundColor: 'transparent', minHeight: '100vh', pt: 3, pb: 8, px: 2 }}>
      <Box mb={3}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: '0.85rem', mb: 1 }}>
          <Link to="/home" className="text-interactive" style={{ textDecoration: 'none', fontWeight: 500, color: 'var(--edge-blue)' }}>
            Dashboard
          </Link>
          <Typography className="text-secondary" sx={{ fontSize: '0.85rem', fontWeight: 500, color: isDark ? '#94A3B8' : '#64748B' }}>
            User Profile
          </Typography>
        </Breadcrumbs>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, fontSize: '1.75rem', color: isDark ? '#F8FAFC' : 'var(--vault-navy)' }}>
          User Profile
        </Typography>
        <Typography variant="body2" sx={{ color: isDark ? '#94A3B8' : '#64748B' }}>
          View and manage user information and account details.
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <ProfileSummary
            displayName={displayName}
            designation={designation}
            email={currentUser.email}
            phone={phone}
          />
        </Grid>
        <Grid item xs={12} md={8}>
          <DetailCard
            title="Personal Details"
            icon="person"
            action={
              <Button
                variant="outlined"
                startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                onClick={handleOpenEdit}
                size="small"
                sx={{
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontWeight: 600,
                  color: isDark ? '#F8FAFC' : 'var(--vault-navy)',
                  borderColor: isDark ? 'rgba(255,255,255,0.2)' : 'var(--hairline)',
                  '&:hover': { backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : '#f8fafc' },
                }}
              >
                Edit
              </Button>
            }
            items={[
              { label: 'Name', value: displayName },
              { label: 'Email Address', value: currentUser.email || '—' },
              { label: 'Phone Number', value: phone || '—' },
              { label: 'Department', value: department || '—' },
              { label: 'Employee ID', value: empId || '—' },
              { label: 'Designation', value: designation || '—' },
            ]}
          />
          <DetailCard
            title="Account Details"
            icon="badge"
            items={[
              { label: 'Role', value: role || '—' },
              { label: 'Username', value: currentUser.username || currentUser.name || '—' },
            ]}
          />
          
          <Card sx={{ mb: 4, boxShadow: 'none', border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid var(--hairline)', borderRadius: '16px', backgroundColor: isDark ? 'var(--canvas)' : '#FFF' }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Box sx={{ width: 40, height: 40, borderRadius: '8px', backgroundColor: detailIconMapping['devices']?.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {detailIconMapping['devices']?.icon}
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: isDark ? '#F8FAFC' : 'var(--vault-navy)' }}>
                  Login Devices
                </Typography>
              </Box>

              {devicesLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : devices.length === 0 ? (
                <Box sx={{ 
                  backgroundColor: isDark ? 'rgba(255,255,255,0.02)' : '#F8FAFC', 
                  borderRadius: '8px', 
                  p: 4, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  gap: 2
                }}>
                  <Box sx={{ position: 'relative' }}>
                    <Box sx={{ width: 64, height: 64, borderRadius: '50%', backgroundColor: 'var(--blue-tint)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <DesktopWindowsIcon sx={{ color: 'var(--edge-blue)', fontSize: 32 }} />
                    </Box>
                  </Box>
                  <Typography variant="body2" sx={{ color: isDark ? '#94A3B8' : '#64748B', fontWeight: 500 }}>
                    No devices found for this user.
                  </Typography>
                </Box>
              ) : (
                <List dense>
                  {devices.map((d, idx) => (
                    <React.Fragment key={d.deviceId + '_' + idx}>
                      <ListItem disableGutters sx={{ py: 1.5 }}>
                        <ListItemText
                          primary={<Typography sx={{ fontWeight: 600, color: isDark ? '#F8FAFC' : 'var(--vault-navy)' }}>{d.deviceId}</Typography>}
                          secondary={
                            <Typography variant="caption" sx={{ color: isDark ? '#94A3B8' : '#64748B' }}>
                              Last seen: {d.lastSeen ? format(new Date(d.lastSeen), 'PP p') : '—'}
                            </Typography>
                          }
                        />
                      </ListItem>
                      {idx < devices.length - 1 && <Divider component="li" sx={{ borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'var(--hairline)' }} />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Edit Profile Modal Dialog */}
      <Dialog
        open={editOpen}
        onClose={() => !editLoading && setEditOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: isDark ? '#111827' : '#ffffff',
            backgroundImage: 'none',
            borderRadius: '16px',
            boxShadow: isDark ? '0 10px 25px rgba(0,0,0,0.5)' : '0 10px 25px rgba(0,0,0,0.1)',
            border: isDark ? '1px solid rgba(255,255,255,0.1)' : 'none',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 3,
            pb: 2,
            borderBottom: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid #E2E8F0',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, color: isDark ? '#F8FAFC' : '#0F172A' }}>
            Edit Profile Details
          </Typography>
          <IconButton
            size="small"
            onClick={() => setEditOpen(false)}
            disabled={editLoading}
            sx={{ color: isDark ? '#94A3B8' : '#64748B' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <form onSubmit={handleSaveProfile}>
          <DialogContent sx={{ p: 3 }}>
            {/* Avatar Upload Area */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3 }}>
              <Box sx={{ position: 'relative' }}>
                {editFormData.profile_image_preview ? (
                  <Box
                    component="img"
                    src={editFormData.profile_image_preview}
                    alt="Preview"
                    sx={{ width: 72, height: 72, borderRadius: '50%', objectFit: 'cover' }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: 72,
                      height: 72,
                      borderRadius: '50%',
                      backgroundColor: 'var(--blue-tint)',
                      color: 'var(--edge-blue)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.75rem',
                      fontWeight: 600,
                    }}
                  >
                    {editFormData.first_name ? editFormData.first_name[0].toUpperCase() : 'U'}
                  </Box>
                )}
                <IconButton
                  size="small"
                  onClick={() => fileInputRef.current?.click()}
                  sx={{
                    position: 'absolute',
                    right: -4,
                    bottom: -4,
                    backgroundColor: '#3B82F6',
                    color: '#ffffff',
                    width: 28,
                    height: 28,
                    '&:hover': { backgroundColor: '#2563EB' },
                  }}
                >
                  <CameraAltIcon sx={{ fontSize: 16 }} />
                </IconButton>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleAvatarFileChange}
                />
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: isDark ? '#F8FAFC' : '#0F172A' }}>
                  Profile Picture
                </Typography>
                <Typography variant="caption" sx={{ color: isDark ? '#94A3B8' : '#64748B', display: 'block', mb: 1 }}>
                  JPG, PNG or GIF up to 5MB
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => fileInputRef.current?.click()}
                  sx={{
                    textTransform: 'none',
                    fontSize: '12px',
                    borderColor: isDark ? 'rgba(255,255,255,0.2)' : '#CBD5E1',
                    color: isDark ? '#F8FAFC' : '#334155',
                  }}
                >
                  Upload New Photo
                </Button>
              </Box>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.75, color: isDark ? '#E2E8F0' : '#1E293B', fontSize: '13px' }}>
                  First Name <span style={{ color: '#EF4444' }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  name="first_name"
                  placeholder="Enter first name"
                  value={editFormData.first_name}
                  onChange={handleEditChange}
                  sx={inputStyle}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.75, color: isDark ? '#E2E8F0' : '#1E293B', fontSize: '13px' }}>
                  Last Name
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  name="last_name"
                  placeholder="Enter last name"
                  value={editFormData.last_name}
                  onChange={handleEditChange}
                  sx={inputStyle}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.75, color: isDark ? '#E2E8F0' : '#1E293B', fontSize: '13px' }}>
                  Email Address
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  type="email"
                  name="email"
                  placeholder="Enter email address"
                  value={editFormData.email}
                  disabled
                  sx={inputStyle}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.75, color: isDark ? '#E2E8F0' : '#1E293B', fontSize: '13px' }}>
                  Mobile Number
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  name="mobile"
                  placeholder="Enter mobile number"
                  value={editFormData.mobile}
                  onChange={handleEditChange}
                  inputProps={{ maxLength: 10, inputMode: 'numeric' }}
                  sx={inputStyle}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.75, color: isDark ? '#E2E8F0' : '#1E293B', fontSize: '13px' }}>
                  Employee ID
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  name="emp_id"
                  placeholder="Enter employee ID"
                  value={editFormData.emp_id}
                  onChange={handleEditChange}
                  inputProps={{ maxLength: 8 }}
                  sx={inputStyle}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.75, color: isDark ? '#E2E8F0' : '#1E293B', fontSize: '13px' }}>
                  Designation
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  name="designation"
                  placeholder="Enter designation"
                  value={editFormData.designation}
                  onChange={handleEditChange}
                  sx={inputStyle}
                />
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions
            sx={{
              p: 3,
              pt: 2,
              borderTop: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid #E2E8F0',
            }}
          >
            <Button
              onClick={() => setEditOpen(false)}
              disabled={editLoading}
              sx={{ textTransform: 'none', color: isDark ? '#94A3B8' : '#64748B', fontWeight: 600 }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={editLoading}
              sx={{
                textTransform: 'none',
                backgroundColor: '#3B82F6',
                '&:hover': { backgroundColor: '#2563EB' },
                fontWeight: 600,
                px: 3,
              }}
            >
              {editLoading ? <CircularProgress size={20} color="inherit" /> : 'Save Changes'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnack(prev => ({ ...prev, open: false }))}
          severity={snack.severity}
          variant="filled"
          sx={{ width: '100%', borderRadius: '8px' }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Profile;

